import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetCurrentUser } from '@/api/generated';
import { LOCALES } from '@/i18n';

/**
 * When the current user has a stored preferredLocale, apply it to i18n so it wins over
 * LanguageDetector (localStorage / navigator).
 *
 * Returns query state and `preferredLocaleFromApi` when it is a supported platform locale
 * (use `preferredLocaleFromApi ?? EN_US_LOCALE` for onboarding defaults when unset).
 */
export const useSyncI18nWithPreferredLocale = () => {
  const { i18n } = useTranslation();
  const { data: currentUser, isLoading: isCurrentUserLoading } = useGetCurrentUser();

  const preferredLocaleFromApi = useMemo(() => {
    const p = currentUser?.preferredLocale;
    if (!p || !LOCALES.some(code => code === p)) return undefined;

    return p;
  }, [currentUser?.preferredLocale]);

  useEffect(() => {
    if (!preferredLocaleFromApi) return;
    void i18n.changeLanguage(preferredLocaleFromApi);
  }, [preferredLocaleFromApi, i18n]);

  return {
    isCurrentUserLoading,
    currentUser,
    preferredLocaleFromApi
  };
};
